import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader.js";
import type { ModelConfig } from "@visorxr/core";

/** Descriptor sentinel that renders a built-in procedural sign (dev/demo without an art asset). */
export const PLACEHOLDER_MODEL = "placeholder";

// GLTFLoader with a self-hosted Draco decoder (glTF geometry compression — high-poly signage
// compresses ~10-20×). Attaching a DRACOLoader is inert for uncompressed glTFs, so it's safe for
// every model; the decoder is served from /draco/ (packages/player/public/draco).
function createGltfLoader(): GLTFLoader {
  const draco = new DRACOLoader().setDecoderPath("/draco/");
  return new GLTFLoader().setDRACOLoader(draco);
}

/**
 * Load a glTF sign and wrap it so its placement origin sits at the group origin. `scale` and
 * `yOffset` (a model-space lift, distinct from the placement height) are applied here. A real URL
 * that fails to load rejects (fail loud — the model is the content); only the explicit
 * `"placeholder"` sentinel substitutes the procedural sign.
 */
export async function loadSignage(config: ModelConfig): Promise<THREE.Object3D> {
  if (config.url === PLACEHOLDER_MODEL) {
    return createPlaceholderSignage(config.scale ?? 1);
  }

  const gltf = await createGltfLoader().loadAsync(config.url);

  const model = gltf.scene;
  model.scale.setScalar(config.scale ?? 1);
  model.position.y += config.yOffset ?? 0;

  const group = new THREE.Group();
  group.add(model);
  return group;
}

/** A procedural tall sign (panel on a board) so the player renders content with no glTF asset. */
export function createPlaceholderSignage(scale = 1): THREE.Object3D {
  const board = new THREE.Mesh(
    new THREE.BoxGeometry(2.4, 3.2, 0.2),
    new THREE.MeshStandardMaterial({ color: 0x2563eb, roughness: 0.5, metalness: 0.1 }),
  );
  const panel = new THREE.Mesh(
    new THREE.BoxGeometry(2.0, 2.8, 0.06),
    new THREE.MeshStandardMaterial({ color: 0xe7eaee, roughness: 0.85 }),
  );
  panel.position.z = 0.13;
  board.add(panel);

  const group = new THREE.Group();
  group.add(board);
  group.scale.setScalar(scale);
  return group;
}
